// ─── Image Validator (Client-side guard) ───
// Kling API 호환 기준: JPG/PNG/WebP · ≤10MB · 각 변 ≥300px · 긴 변 ≤2048px · 비율 1:2.5~2.5:1
// 사용처: 인쇄 미리보기, Omni 합성, 부품 조립 등 로컬 파일 업로드 전 검증
// ──────────────────────────────────────────────────

export interface ImageValidationResult {
  ok: boolean;
  code: ImageValidationErrorCode | null;
  message: string | null;
  width?: number;
  height?: number;
}

export type ImageValidationErrorCode =
  | 'invalid_format'
  | 'heic_not_supported'
  | 'file_too_large'
  | 'too_small'
  | 'too_large_dimension'
  | 'bad_aspect'
  | 'unreadable';

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const MIN_EDGE = 300;
const MAX_LONG_EDGE = 2048;
const MIN_ASPECT = 1 / 2.5; // short / long ≥ 0.4 → 1:2.5 ~ 2.5:1

const ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

const ALLOWED_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'webp',
]);

// iOS HEIC 방지
const HEIC_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

// 미리보기·연속 생성 등 대용량 파일이면 JPEG 변환 제안할 수 있음
export const PNG_SIZE_WARN_THRESHOLD = 3 * 1024 * 1024; // 3MB — PNG일 때 "JPEG 변환 권장" 안내 가능

/**
 * PC에서 선택한 이미지 파일을 서버 업로드 전에 검증합니다.
 * 통과 시 { ok: true } + width/height, 실패 시 한국어 오류 메시지와 허용 규격 안내.
 */
export async function validateImageFile(file: File): Promise<ImageValidationResult> {
  // ─── 1. 포맷 검사 (MIME + 확장자 이중 확인) ───
  const mimeType = (file.type || '').toLowerCase();
  const rawExt = file.name.split('.').pop()?.toLowerCase() || '';
  const ext = rawExt.replace(/^\.+/, '');

  // HEIC/HEIF 명시적 거절 (iOS 사용자에게 JPG 변환 안내)
  if (HEIC_TYPES.has(mimeType) || ext === 'heic' || ext === 'heif') {
    return {
      ok: false,
      code: 'heic_not_supported',
      message: 'HEIC/HEIF 파일은 지원하지 않습니다. JPG 또는 PNG로 변환 후 올려 주세요.',
    };
  }

  const isAllowedType = ALLOWED_TYPES.has(mimeType);
  const isAllowedExt = ALLOWED_EXTENSIONS.has(ext);

  // MIME이 비어있거나 이상한 경우 확장자로 판단
  if (!isAllowedType && !isAllowedExt) {
    return {
      ok: false,
      code: 'invalid_format',
      message: 'JPG, PNG, WebP 파일만 올릴 수 있습니다. HEIC/HEIF, GIF, BMP, TIFF, PDF, AI, SVG 등은 지원하지 않습니다.',
    };
  }

  // ─── 2. 용량 검사 ───
  if (file.size > MAX_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      code: 'file_too_large',
      message: `파일 크기는 10MB 이하여야 합니다. (현재 ${sizeMB}MB)`,
    };
  }

  // ─── 3. 이미지 읽기 + 해상도/비율 검사 ───
  let img: ImageBitmap | null = null;
  try {
    img = await createImageBitmap(file);
    const width = img.width;
    const height = img.height;

    // 각 변 최소 300px
    if (width < MIN_EDGE || height < MIN_EDGE) {
      return {
        ok: false,
        code: 'too_small',
        message: `가로·세로 모두 ${MIN_EDGE}px 이상이어야 합니다. (현재 ${width}x${height})`,
      };
    }

    // 긴 변 최대 2048px
    const long = Math.max(width, height);
    if (long > MAX_LONG_EDGE) {
      return {
        ok: false,
        code: 'too_large_dimension',
        message: `긴 변은 ${MAX_LONG_EDGE}px 이하여야 합니다. (현재 ${long}px)`,
      };
    }

    // 종횡비 검사: min(w,h) / max(w,h) ≥ 1/2.5
    const short = Math.min(width, height);
    if (short / long < MIN_ASPECT) {
      return {
        ok: false,
        code: 'bad_aspect',
        message: `이미지 비율은 1:2.5 ~ 2.5:1 이내여야 합니다. (현재 ${width}x${height})`,
      };
    }

    return { ok: true, code: null, message: null, width, height };
  } catch {
    return {
      ok: false,
      code: 'unreadable',
      message: '이미지를 읽을 수 없습니다. 파일이 손상되었거나 지원하지 않는 형식입니다.',
    };
  } finally {
    img?.close();
  }
}

/**
 * PNG 파일이고 용량이 3MB를 초과하면 JPEG 변환을 제안할 수 있는지 반환합니다.
 */
export function shouldSuggestJpegConversion(file: File): boolean {
  const mimeType = (file.type || '').toLowerCase();
  return mimeType === 'image/png' && file.size > PNG_SIZE_WARN_THRESHOLD;
}

/**
 * 검증 실패 시 사용자에게 보여줄 허용 규격 요약 텍스트 (UI 하단 안내용)
 */
export function getAllowedSpecsSummary(): string {
  return '허용 규격: JPG, PNG, WebP / 최대 10MB / 가로·세로 300px 이상 / 긴 변 2,048px 이하 / 비율 1:2.5 ~ 2.5:1';
}